import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ExternalLink, MapPin, MessageCircle, Pin, SearchX } from "lucide-react";
import api from "../lib/api";
import { LOCALITIES, PLATFORM_META, waLink } from "../data/site";
import { EASE } from "../lib/motion";
import Reveal from "./Reveal";

const TYPE_TABS = [
  { id: "all", label: "All Listings" },
  { id: "New Project", label: "New Projects" },
  { id: "Resale", label: "Resale Flats" },
];

const statusTone = (status = "") =>
  status.toLowerCase().includes("ready")
    ? "bg-wa/15 text-[#136d3c] border-wa/30"
    : status.toLowerCase().includes("new launch")
      ? "bg-gold/15 text-[#7a5b0e] border-gold/40"
      : "bg-navy/5 text-navy/70 border-navy/15";

const hostOf = (url = "") => {
  try {
    return new URL(url).hostname.replace("www.", "");
  } catch {
    return "";
  }
};

export const Projects = () => {
  const [type, setType] = useState("all");
  const [locality, setLocality] = useState("all");

  const { data: projects, isLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => (await api.get("/api/projects")).data,
  });

  const list = (projects || []).filter(
    (p) =>
      (type === "all" || p.listing_type === type) &&
      (locality === "all" || p.location === locality)
  );
  const ordered = [...list].sort(
    (a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)
  );
  const typeCount = (t) =>
    (projects || []).filter((p) => t === "all" || p.listing_type === t).length;

  return (
    <section id="inventory" className="relative py-24 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="max-w-3xl">
            <div className="mb-5 flex items-center gap-4">
              <span className="gold-rule" />
              <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-gold-deep">
                Live Listings — Borivali To Andheri
              </p>
            </div>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              New projects &amp; resale flats,{" "}
              <span className="text-gold-gradient italic">both sides of the line.</span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-navy/70">
              Tracked across Housing.com &amp; 99acres and updated by our team
              every week. Filter by locality, then message us — we confirm
              availability, actual pricing and the best floor before you visit.
            </p>
          </div>
        </Reveal>

        <Reveal className="mt-10" delay={0.05}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-2" data-testid="listing-type-tabs">
              {TYPE_TABS.map((t) => (
                <button
                  key={t.id}
                  data-testid={`type-tab-${t.id === "all" ? "all" : t.id.split(" ")[0].toLowerCase()}`}
                  onClick={() => setType(t.id)}
                  className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-all ${
                    type === t.id
                      ? "border-navy bg-navy text-white"
                      : "border-[#E7E3D8] bg-white text-navy/70 hover:border-navy/40"
                  }`}
                >
                  {t.label}
                  <span className={`ml-2 text-xs ${type === t.id ? "text-gold-light" : "text-gold-deep"}`}>
                    {typeCount(t.id)}
                  </span>
                </button>
              ))}
            </div>
            <div className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1 sm:mx-0 sm:flex-wrap sm:px-0" data-testid="locality-filter-chips">
              {["all", ...LOCALITIES].map((loc) => {
                const n =
                  loc === "all"
                    ? (projects || []).length
                    : (projects || []).filter((p) => p.location === loc).length;
                return (
                  <button
                    key={loc}
                    data-testid={`locality-chip-${loc.toLowerCase().replace(/ /g, "-")}`}
                    onClick={() => setLocality(loc)}
                    className={`shrink-0 rounded-full border px-4 py-2 text-[13px] font-medium transition-all ${
                      locality === loc
                        ? "border-gold-deep bg-gold/15 text-[#7a5b0e]"
                        : "border-[#E7E3D8] bg-white text-navy/60 hover:border-navy/30"
                    }`}
                  >
                    {loc === "all" ? "All localities" : loc}
                    {n ? ` · ${n}` : ""}
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>

        {isLoading ? (
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                data-testid="listing-card-skeleton"
                className="h-64 animate-pulse rounded-2xl border border-[#E7E3D8] bg-white"
              />
            ))}
          </div>
        ) : list.length === 0 ? (
          <Reveal className="mt-12">
            <div
              data-testid="listings-empty-state"
              className="rounded-2xl border border-[#E7E3D8] bg-white p-12 text-center"
            >
              <SearchX className="mx-auto h-8 w-8 text-gold-deep" />
              <p className="mt-4 font-serif text-2xl font-semibold text-navy">
                Nothing listed here right now
              </p>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-navy/60">
                Our off-portal network may still have options in this pocket.
                Tell us what you need on WhatsApp and we&apos;ll shortlist within
                the hour.
              </p>
              <a
                href={waLink(
                  `Hello Shivaay Consultant! I'm looking in ${locality === "all" ? "Borivali–Andheri" : locality}. Please share matching options.`
                )}
                target="_blank"
                rel="noreferrer"
                data-testid="listings-empty-whatsapp"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-wa px-6 py-3 text-sm font-bold text-white"
              >
                <MessageCircle className="h-4 w-4" />
                Ask on WhatsApp
              </a>
            </div>
          </Reveal>
        ) : (
          <motion.div layout className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {ordered.map((p) => (
                <motion.article
                  key={p.id}
                  layout
                  initial={{ opacity: 0, scale: 0.96, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96, y: 20 }}
                  transition={{ duration: 0.35, ease: EASE }}
                  data-testid={`listing-card-${p.id}`}
                  className="group relative flex h-full flex-col rounded-2xl border border-[#E7E3D8] bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-gold-deep/50 hover:shadow-[0_18px_44px_rgba(14,32,56,0.1)]"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${
                        p.listing_type === "Resale"
                          ? "border-navy/20 bg-navy/5 text-navy"
                          : "border-gold/50 bg-gold/15 text-[#7a5b0e]"
                      }`}
                    >
                      {p.listing_type}
                    </span>
                    <div className="flex items-center gap-2">
                      {p.featured && (
                        <span
                          data-testid={`priority-badge-${p.id}`}
                          className="flex items-center gap-1 rounded-full border border-gold/60 bg-gold/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#7a5b0e]"
                        >
                          <Pin className="h-3 w-3" />
                          Priority
                        </span>
                      )}
                      <div className="flex gap-1.5">
                        {(p.platforms || []).map((k) => (
                          <span
                            key={k}
                            title={`Tracked on ${PLATFORM_META[k]?.label || k}`}
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ background: PLATFORM_META[k]?.color || "#888" }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <h3 className="mt-4 font-serif text-[1.4rem] font-semibold leading-snug text-navy">
                    {p.name}
                  </h3>
                  <p className="mt-1.5 flex items-center gap-1.5 text-sm font-medium text-navy/60">
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-gold-deep" />
                    {p.location}
                  </p>

                  <div className="mt-5 flex items-end justify-between gap-3 border-t border-[#EFEAE0] pt-4">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-navy/40">
                        Price
                      </p>
                      <p className="mt-0.5 font-serif text-xl font-semibold text-gold-deep">
                        {p.price || "On request"}
                      </p>
                    </div>
                    {p.config && (
                      <div className="text-right">
                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-navy/40">
                          Config
                        </p>
                        <p className="mt-0.5 text-sm font-semibold text-navy">{p.config}</p>
                      </div>
                    )}
                  </div>

                  <span
                    className={`mt-4 w-fit rounded-full border px-3 py-1 text-[11px] font-semibold ${statusTone(p.status)}`}
                  >
                    {p.status}
                  </span>
                  {p.note && (
                    <p className="mt-3 text-[13px] leading-relaxed text-navy/55">{p.note}</p>
                  )}

                  <div className="mt-auto flex items-center gap-2 border-t border-[#EFEAE0] pt-4" style={{ marginTop: "1.25rem" }}>
                    <a
                      href={waLink(
                        `Hello Shivaay Consultant! I'd like details, current availability and the best price for ${p.name} in ${p.location}.`
                      )}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`card-whatsapp-${p.id}`}
                      className="flex flex-1 items-center justify-center gap-2 rounded-full bg-wa px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#189a51]"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Enquire
                    </a>
                    {p.source_url && (
                      <a
                        href={p.source_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`card-source-${p.id}`}
                        title={`View on ${hostOf(p.source_url)}`}
                        className="flex items-center gap-1.5 rounded-full border border-[#E7E3D8] px-4 py-2.5 text-xs font-semibold text-navy/60 transition-colors hover:border-gold-deep hover:text-gold-deep"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        {hostOf(p.source_url)}
                      </a>
                    )}
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        <Reveal className="mt-10" delay={0.1}>
          <p className="text-center text-xs leading-relaxed text-navy/45">
            Prices &amp; availability as publicly listed on Housing.com / 99acres
            (Nov 2026) — they change weekly. We verify the current picture on
            WhatsApp before any site visit.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Projects;
